import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Check, Loader2, Send } from "lucide-react";
import { Container, PageHeader, buttonClass } from "../components/site";
import { Reveal } from "../components/motion";
import { useToast } from "../hooks/use-toast";
import { useI18n } from "../i18n";
import axios from "axios";
import getApiBaseUrl from "../utils/apiBaseUrl";

const BACKEND_URL = getApiBaseUrl();

// Translation keys for each service, in the same order as `services` below.
const SERVICE_IDS = ["ai-automation", "web-development", "python-automation", "ai-video"];

const Checkout = () => {
  const location = useLocation();
  const { toast } = useToast();
  const { t } = useI18n();
  const preSelectedService = location.state || null;

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    service: preSelectedService?.service || "",
    plan: preSelectedService?.plan || "",
    additionalInfo: "",
    termsAccepted: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Values (not labels) are sent to the backend, so they stay in English.
  const services = [
    {
      name: "AI Automation Systems",
      plans: [
        { name: "Starter Automation", price: 25999 },
        { name: "Business Automation Suite", price: 59999 },
        { name: "Enterprise AI Systems", price: 149999 }
      ]
    },
    {
      name: "Custom Website Development",
      plans: [
        { name: "Static Business Website", price: 14999 },
        { name: "Dynamic Website with Admin", price: 29999 },
        { name: "Full Custom Web App", price: 59999 }
      ]
    },
    {
      name: "Python Automation & Data Solutions",
      plans: [
        { name: "Simple Automation Script", price: 9999 },
        { name: "Web Scraping Solution", price: 19999 },
        { name: "Complex Data Pipeline", price: 39999 }
      ]
    },
    {
      name: "AI Video & Avatar Systems",
      plans: [
        { name: "Basic AI Video Setup", price: 29999 },
        { name: "Professional Avatar System", price: 59999 },
        { name: "Enterprise Video Platform", price: 129999 }
      ]
    }
  ];

  const selectedServiceData = services.find(s => s.name === formData.service);
  const selectedPlanData = selectedServiceData?.plans.find(p => p.name === formData.plan);
  const subtotal = selectedPlanData?.price || 0;
  const gst = Math.round(subtotal * 0.18);
  const total = subtotal + gst;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value
    });
  };

  const handleServiceChange = (e) => {
    setFormData({
      ...formData,
      service: e.target.value,
      plan: ""
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.termsAccepted) {
      toast({
        title: t("checkout.termsTitle"),
        description: t("checkout.termsBody"),
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const checkoutData = {
        ...formData,
        amount: total,
        subtotal: subtotal,
        gst: gst
      };

      await axios.post(`${BACKEND_URL}/checkout`, checkoutData);

      toast({
        title: t("checkout.okTitle"),
        description: t("checkout.okBody"),
      });

      // In production, redirect to payment gateway
      // For now, just reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        service: "",
        plan: "",
        additionalInfo: "",
        termsAccepted: false
      });
    } catch (error) {
      toast({
        title: t("checkout.errTitle"),
        description: t("checkout.errBody"),
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Display labels for the selected English values.
  const serviceIdx = services.findIndex((s) => s.name === formData.service);
  const serviceId = SERVICE_IDS[serviceIdx];
  const serviceLabel = (idx) => t(`services.items.${SERVICE_IDS[idx]}.title`);
  const planLabel = (idx, planIdx) => t(`services.items.${SERVICE_IDS[idx]}.plans.${planIdx}.name`);
  const selectedPlanIdx = selectedServiceData ? selectedServiceData.plans.findIndex((p) => p.name === formData.plan) : -1;
  const optional = <span className="font-normal text-ink-3">({t("common.optional")})</span>;
  const policyLink = "text-brand underline underline-offset-2";

  return (
    <>
      <Header />

      <PageHeader eyebrow={t("checkout.eyebrow")} title={t("checkout.title")} lede={t("checkout.lede")} />

      <section className="py-16 md:py-20">
        <Container className="grid gap-8 lg:grid-cols-12">
          <form onSubmit={handleSubmit} className="space-y-5 lg:col-span-8">
            <Reveal as="fieldset" className="rounded-[2rem] border border-line bg-surface p-6 md:p-9">
              <legend className="sr-only">{t("checkout.details")}</legend>
              <h2 className="mb-6 flex items-center gap-3 text-lg font-semibold text-ink">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-soft font-mono text-xs text-brand">1</span>
                {t("checkout.details")}
              </h2>
              <div className="space-y-5">
                <div className="grid gap-5 md:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="field-label">{t("checkout.name")}</label>
                    <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required autoComplete="name" className="field" />
                  </div>
                  <div>
                    <label htmlFor="company" className="field-label">
                      {t("checkout.company")} {optional}
                    </label>
                    <input type="text" id="company" name="company" value={formData.company} onChange={handleChange} autoComplete="organization" className="field" />
                  </div>
                </div>
                <div className="grid gap-5 md:grid-cols-2">
                  <div>
                    <label htmlFor="email" className="field-label">{t("checkout.email")}</label>
                    <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required autoComplete="email" className="field" placeholder="you@company.com" dir="ltr" />
                  </div>
                  <div>
                    <label htmlFor="phone" className="field-label">{t("checkout.phone")}</label>
                    <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required autoComplete="tel" className="field" placeholder="+91" dir="ltr" />
                  </div>
                </div>
              </div>
            </Reveal>

            <Reveal as="fieldset" delay={100} className="rounded-[2rem] border border-line bg-surface p-6 md:p-9">
              <legend className="sr-only">{t("checkout.project")}</legend>
              <h2 className="mb-6 flex items-center gap-3 text-lg font-semibold text-ink">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-soft font-mono text-xs text-brand">2</span>
                {t("checkout.project")}
              </h2>
              <div className="space-y-5">
                <div>
                  <label htmlFor="service" className="field-label">{t("checkout.service")}</label>
                  <select id="service" name="service" value={formData.service} onChange={handleServiceChange} required className="field">
                    <option value="">{t("checkout.selectService")}</option>
                    {services.map((service, idx) => (
                      <option key={service.name} value={service.name}>
                        {serviceLabel(idx)}
                      </option>
                    ))}
                  </select>
                </div>

                {formData.service && (
                  <div className="pop-in">
                    <label htmlFor="plan" className="field-label">{t("checkout.option")}</label>
                    <select id="plan" name="plan" value={formData.plan} onChange={handleChange} required className="field">
                      <option value="">{t("checkout.selectOption")}</option>
                      {selectedServiceData?.plans.map((plan, planIdx) => (
                        <option key={plan.name} value={plan.name}>
                          {planLabel(serviceIdx, planIdx)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label htmlFor="additionalInfo" className="field-label">
                    {t("checkout.notes")} {optional}
                  </label>
                  <textarea
                    id="additionalInfo"
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleChange}
                    rows="4"
                    className="field resize-y"
                    placeholder={t("checkout.notesPh")}
                  ></textarea>
                </div>
              </div>
            </Reveal>

            <Reveal delay={200} className="rounded-[2rem] border border-line bg-surface p-6 md:p-9">
              <label htmlFor="termsAccepted" className="flex cursor-pointer items-start gap-3 text-[0.9375rem] leading-relaxed text-ink-2">
                <input
                  type="checkbox"
                  id="termsAccepted"
                  name="termsAccepted"
                  checked={formData.termsAccepted}
                  onChange={handleChange}
                  className="mt-1 h-4 w-4 shrink-0 accent-brand"
                />
                <span>
                  {t("checkout.agree")}{" "}
                  <a href="/terms" target="_blank" className={policyLink}>{t("footer.legal.terms")}</a>,{" "}
                  <a href="/privacy-policy" target="_blank" className={policyLink}>{t("footer.legal.privacy")}</a>,{" "}
                  <a href="/refund-policy" target="_blank" className={policyLink}>{t("footer.legal.refund")}</a> {t("checkout.and")}{" "}
                  <a href="/cancellation-policy" target="_blank" className={policyLink}>{t("footer.legal.cancellation")}</a>.
                </span>
              </label>

              <button
                type="submit"
                disabled={isSubmitting || !formData.service || !formData.plan}
                className={`${buttonClass("primary", "lg")} mt-6 w-full`}
              >
                {isSubmitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} className="rtl:-scale-x-100" />}
                {isSubmitting ? t("checkout.submitting") : t("checkout.submit")}
              </button>
            </Reveal>
          </form>

          <aside className="lg:col-span-4">
            <Reveal delay={150} className="sticky top-28 rounded-[2rem] border border-line bg-surface p-6 md:p-7">
              <h2 className="text-lg font-semibold text-ink">{t("checkout.summary")}</h2>

              {formData.service && formData.plan ? (
                <dl key={formData.plan} className="pop-in mt-6 space-y-4 text-[0.9375rem]">
                  <div>
                    <dt className="text-sm text-ink-3">{t("checkout.summaryService")}</dt>
                    <dd className="mt-0.5 text-ink">{serviceId ? serviceLabel(serviceIdx) : formData.service}</dd>
                  </div>
                  <div>
                    <dt className="text-sm text-ink-3">{t("checkout.summaryOption")}</dt>
                    <dd className="mt-0.5 font-medium text-ink">
                      {selectedPlanIdx >= 0 ? planLabel(serviceIdx, selectedPlanIdx) : formData.plan}
                    </dd>
                  </div>
                  <p className="rounded-2xl bg-brand-soft p-4 text-sm leading-relaxed text-ink">{t("checkout.next")}</p>
                </dl>
              ) : (
                <p className="mt-6 rounded-2xl border border-dashed border-line px-4 py-8 text-center text-sm text-ink-3">
                  {t("checkout.empty")}
                </p>
              )}

              <ul className="mt-6 space-y-2.5 border-t border-line pt-5 text-sm text-ink-2">
                {t("checkout.perks").map((perk, i) => (
                  <li key={perk} className="flex gap-2.5">
                    <Check size={16} className="mt-0.5 shrink-0 text-brand" />
                    {i === 1 ? (
                      <Link to="/refund-policy" className="underline-offset-2 hover:underline">
                        {perk}
                      </Link>
                    ) : (
                      perk
                    )}
                  </li>
                ))}
              </ul>
            </Reveal>
          </aside>
        </Container>
      </section>

      <Footer />
    </>
  );
};

export default Checkout;
